import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Clock3, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { usePlanningContext } from '../state/PlanningContext';

const STATUS_OPTIONS = [
  {
    value: 'all',
    label: 'All statuses',
    description: 'Show applications with any decision status',
    icon: Layers,
    dotClass: 'bg-[var(--c-accent)]'
  },
  {
    value: 'pending',
    label: 'Pending',
    description: 'Awaiting assessment or decision',
    icon: Clock3,
    dotClass: 'bg-[#D3AF37]'
  },
  {
    value: 'decided',
    label: 'Decided',
    description: 'Approved, granted or otherwise determined',
    icon: CheckCircle2,
    dotClass: 'bg-[#2EA86B]'
  },
  {
    value: 'refused',
    label: 'Refused',
    description: 'Refused or rejected applications',
    icon: XCircle,
    dotClass: 'bg-[#D25B5B]'
  },
  {
    value: 'withdrawn',
    label: 'Withdrawn',
    description: 'Applications withdrawn by applicant',
    icon: XCircle,
    dotClass: 'bg-[#8FA2B8]'
  }
];

const DecisionStatusFilter = () => {
  const { decisionStatusFilter, setDecisionStatusFilter } = usePlanningContext();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [portalMounted, setPortalMounted] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => { setPortalMounted(true); }, []);

  const activeValue = typeof decisionStatusFilter === 'string'
    ? decisionStatusFilter.toLowerCase().trim()
    : 'all';

  const activeOption = useMemo(() => {
    return STATUS_OPTIONS.find((option) => option.value === activeValue) || STATUS_OPTIONS[0];
  }, [activeValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target)
        && triggerRef.current && !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const isFiltered = activeOption.value !== 'all';
  const ActiveIcon = activeOption.icon;

  const handleSelect = (value) => {
    setDecisionStatusFilter(value);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = Math.min(rect.width, window.innerWidth - 16);
      let left = rect.left;
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = window.innerWidth - dropdownWidth - 8;
      }
      left = Math.max(8, left);

      const minTop = 60;
      const spaceAbove = rect.top - minTop;
      const spaceBelow = window.innerHeight - rect.bottom - 8;

      if (spaceAbove >= spaceBelow) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 8, left, width: dropdownWidth, maxHeight: Math.max(spaceAbove, 200) });
      } else {
        setDropdownPos({ top: rect.bottom + 8, left, width: dropdownWidth, maxHeight: spaceBelow });
      }
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div className="pr-field w-full">
      <span className="pr-field-label">Decision status</span>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={`pr-field-trigger ${isFiltered ? 'pr-field-trigger--active' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Decision status filter"
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <ActiveIcon className={`h-4 w-4 shrink-0 ${isFiltered ? 'text-[var(--c-accent)]' : 'text-[var(--c-text-secondary)]'}`} />
          <span className="truncate">{activeOption.label}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--c-text-secondary)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {portalMounted && createPortal(
        <AnimatePresence>
          {isOpen && dropdownPos && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.16 }}
              style={{
                position: 'fixed',
                left: dropdownPos.left,
                width: dropdownPos.width,
                maxHeight: dropdownPos.maxHeight,
                overflowY: 'auto',
                zIndex: 9999,
                ...(dropdownPos.top !== undefined
                  ? { top: dropdownPos.top }
                  : { bottom: dropdownPos.bottom })
              }}
              className="planning-register border border-[var(--c-border)] border-t-2 border-t-[var(--c-text-primary)] bg-[var(--c-surface)]"
            >
              <div className="border-b border-[var(--c-border)] px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--c-text-tertiary)]">Decision Status</p>
                <p className="mt-1 text-xs text-[var(--c-text-secondary)]">Choose which decision outcomes to include.</p>
              </div>

              <div className="space-y-1.5 p-2">
                {STATUS_OPTIONS.map(({ value, label, description, icon: Icon, dotClass }) => {
                  const isActive = value === activeValue;

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleSelect(value)}
                      className={`flex w-full items-center gap-3 border px-3 py-2 text-left transition ${
                        isActive
                          ? 'border-[var(--c-text-primary)] bg-[var(--c-surface)]'
                          : 'border-transparent hover:border-[var(--c-border)] hover:bg-[var(--c-surface-hover)]'
                      }`}
                      role="option"
                      aria-selected={isActive}
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-[var(--c-border)] text-[var(--c-text-secondary)]">
                        <Icon className="h-4 w-4" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-sm font-medium text-[var(--c-text-primary)]">
                          <span className={`h-2 w-2 rounded-full ${dotClass}`} />
                          {label}
                        </span>
                        <span className="mt-0.5 block text-xs text-[var(--c-text-secondary)]">{description}</span>
                      </span>

                      {isActive && (
                        <span className="pr-data text-[var(--c-accent)]">Active</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default DecisionStatusFilter;
